<template>
  <div>
    <v-btn fab top right small @click="exportDeposits">
      <v-icon>get_app</v-icon>
    </v-btn>
    
    <!-- Export Status Modal -->
    <v-dialog v-model="showExportModal" max-width="500">
      <v-card>
        <v-card-title>
          <span class="headline">Exportação de Recebíveis</span>
        </v-card-title>
        <v-card-text>
          <div v-if="exportStatus === 'processing'">
            <v-progress-circular indeterminate color="primary" class="mr-3"></v-progress-circular>
            <span>Gerando relatório... Aguarde alguns instantes.</span>
          </div>
          <div v-else-if="exportStatus === 'ready'">
            <v-icon color="success" class="mr-3">check_circle</v-icon>
            <span>Relatório pronto para download!</span>
            <br><br>
            <strong>Arquivo:</strong> {{ exportFileName }}
          </div>
          <div v-else-if="exportStatus === 'error'">
            <v-icon color="error" class="mr-3">error</v-icon>
            <span>Erro ao gerar relatório. Tente novamente.</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeExportModal">
            Fechar
          </v-btn>
          <v-btn 
            v-if="exportStatus === 'ready'" 
            color="primary" 
            @click="downloadFileManual"
            :disabled="!exportFileName"
          >
            <v-icon left>get_app</v-icon>
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ActionCable from 'actioncable';
import { exists as tokenExists } from '../../../services/flowit/token';
export default {
  name: 'DepositsReport',
  data() {
    return {
      url: '',
      snackbar: false,
      subscription: null,
      pollInterval: null,
      expectedFileName: null,
      showExportModal: false,
      exportStatus: 'processing', // 'processing', 'ready', 'error'
      exportFileName: null,
      actionCableConnected: false,
      exportInProgress: false,
    };
  },
  computed: {
    ...mapGetters({
      filterParams: 'deposits/filter',
      systemAppName: 'system/appName',
    }),
  },
  methods: {
    exportDeposits() {
      // Prevent multiple exports
      if (this.exportInProgress) {
        return;
      }
      
      this.exportInProgress = true;
      this.showExportModal = true;
      this.exportStatus = 'processing';
      this.exportFileName = null;
      
      this.$store.dispatch('deposits/exportDeposits', this.filterParams);
      
      // Wait a moment for ActionCable to connect, then decide
      setTimeout(() => {
        if (!this.actionCableConnected) {
          this.startPolling();
        }
      }, 1000);
    },

    closeExportModal() {
      this.showExportModal = false;
      this.exportInProgress = false;
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    downloadFileManual() {
      if (this.exportFileName) {
        this.downloadFile(this.exportFileName);
        this.closeExportModal();
      }
    },
    
    startPolling() {
      // Poll every 5 seconds for 120 seconds max (as fallback only)
      let attempts = 0;
      const maxAttempts = 24;
      
      this.pollInterval = setInterval(() => {
        attempts++;
        
        if (attempts >= maxAttempts) {
          clearInterval(this.pollInterval);
          this.exportStatus = 'error';
          this.exportInProgress = false;
          return;
        }
        
        // Check if export is ready
        this.checkExportStatus();
      }, 5000);
    },
    
    checkExportStatus() {
      this.$store.dispatch('deposits/checkExportStatus')
        .then((response) => {
          if (response.data.file_name) {
            clearInterval(this.pollInterval);
            this.exportStatus = 'ready';
            this.exportFileName = response.data.file_name;
            this.exportInProgress = false;
          }
        })
        .catch(error => {
          const status = error.response && error.response.status;
          // Continue polling on 404, stop on other errors
          if (status !== 404) {
            clearInterval(this.pollInterval);
            this.exportStatus = 'error';
            this.exportInProgress = false;
          }
        });
    },
    downloadFile(fileName) {
      // Stop polling if ActionCable worked
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
      
      this.$store.dispatch('deposits/downloadExport', { file_name: fileName })
        .then((response) => {
          // File download initiated successfully
        })
        .catch(error => {
          console.error('Error downloading file:', error);
          this.exportStatus = 'error';
        });
    }
  },
  async created() {
    await this.$store.dispatch('system/fetchSystemConfiguration');
    
    try {
      if (!tokenExists()) {
        console.warn('No valid session token found, skipping ActionCable subscription');
        return;
      }
      
      const sessionToken = window.sessionStorage.getItem(`${this.systemAppName || process.env.VUE_APP_APP_NAME || 'flowit'}-session`);
      
      if (!sessionToken || sessionToken === 'null' || sessionToken === 'undefined') {
        console.warn('Invalid session token, skipping ActionCable subscription');
        return;
      }
      
      const cable = ActionCable.createConsumer(`${process.env.VUE_APP_WEBSOCKET_PROTOCOL}://${process.env.VUE_APP_FLOWIT_URL}/cable?token=${sessionToken}`);
      
      this.subscription = cable.subscriptions.create({
        channel: 'WebNotificationsChannel',
      }, {
        connected: () => {
          this.actionCableConnected = true;
        },
        disconnected: () => {
          this.actionCableConnected = false;
        },
        rejected: () => {
          this.actionCableConnected = false;
        },
        received: (data) => {
          if(data.event == 'UserReporter.finished'){
            if (data.body && data.body.file_name) {
              // Stop polling if it was running
              if (this.pollInterval) {
                clearInterval(this.pollInterval);
                this.pollInterval = null;
              }
              this.exportStatus = 'ready';
              this.exportFileName = data.body.file_name;
              this.exportInProgress = false;
            } else {
              // Fallback for old format
              this.url = data.body;
              this.exportInProgress = false;
            }
          } else if(data.event == 'UserReporter.error'){
            // Stop polling if it was running
            if (this.pollInterval) {
              clearInterval(this.pollInterval);
              this.pollInterval = null;
            }
            this.exportStatus = 'error';
            this.exportInProgress = false;
          }
        }
      });
    } catch (error) {
      console.error('Error creating ActionCable subscription:', error);
    }
  },
  watch: {
    url(newUrl) {
      window.open(newUrl);
      this.snackbar = false;
    }
  },
  
  beforeDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  },
};
</script>
